import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Send, Download, FileText, Loader2, Pencil } from "lucide-react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useSearchParams } from "react-router-dom";
import { EINZEL_LEISTUNGEN_OPTIONS, PAKET_LABEL } from "@/lib/constants";
import useConfirm from "@/components/crm/useConfirm";

const CUSTOM_LEISTUNG = "__custom__";
const LEISTUNG_OPTIONS = [
  PAKET_LABEL.privat,
  PAKET_LABEL.gewerbe,
  ...EINZEL_LEISTUNGEN_OPTIONS,
];

// Rundum-Sorglos-Pakete werden monatlich abgerechnet → Einheit „Monat“ statt „Pauschal“.
const isPaket = (desc) => (desc || "").includes("Rundum-Sorglos-Paket");
const defaultUnit = (desc) => (isPaket(desc) ? "Monat" : "Pauschal");

const STATUS_LABEL = {
  entwurf: "Entwurf", versendet: "Versendet", angenommen: "Angenommen",
  abgelehnt: "Abgelehnt", abgelaufen: "Abgelaufen",
};

function fmt(v) {
  return (Math.round(v * 100) / 100).toLocaleString("de-DE", {
    style: "currency", currency: "EUR",
  });
}

export default function Quotes() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [params, setParams] = useSearchParams();
  const [confirm, confirmDialog] = useConfirm();

  const load = async () => {
    setLoading(true);
    try {
      const [q, c] = await Promise.all([api.get("/quotes"), api.get("/customers")]);
      setItems(q.data); setCustomers(c.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (params.get("new") === "1") {
      setCreating(true);
      params.delete("new"); setParams(params, { replace: true });
    }
    // eslint-disable-next-line
  }, [params]);

  const openPdf = async (id) => {
    const token = localStorage.getItem("blessing_token");
    const url = `${API_BASE}/quotes/${id}/pdf`;
    // Open in new tab via fetch to include auth header
    setBusyId(id);
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      window.open(objUrl, "_blank");
    } finally { setBusyId(null); }
  };

  const sendQuote = async (id) => {
    setBusyId(id);
    try {
      const r = await api.post(`/quotes/${id}/send`);
      if (r.data.email.status === "simulated") {
        toast.warning("Angebot markiert als versendet – E-Mail NICHT gesendet (Microsoft Graph nicht konfiguriert)");
      } else if (r.data.email.status === "error") {
        toast.error("Angebot markiert, aber E-Mail-Versand über Microsoft Graph fehlgeschlagen");
      } else {
        toast.success("Angebot per E-Mail versendet");
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/quotes/${id}/status`, { status });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (q) => {
    if (busyId) return;
    const ok = await confirm({
      title: `Angebot ${q.quote_number} löschen?`,
      description: "Diese Aktion kann nicht rückgängig gemacht werden.",
    });
    if (!ok) return;
    setBusyId(q.id);
    try {
      await api.delete(`/quotes/${q.id}`);
      toast.success("Angebot gelöscht");
      load();
    } catch (e) {
      // 409 = Rechnung existiert bereits – zweite Bestätigung mit Kaskaden-Löschung
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "quote_has_invoice") {
        const both = await confirm({
          title: "Angebot und Rechnung löschen?",
          description:
            `Zu Angebot ${q.quote_number} existiert bereits Rechnung ` +
            `${detail.invoice_number} (Status: ${detail.invoice_status}).\n\n` +
            `Wenn Sie fortfahren, wird sowohl das Angebot als auch die Rechnung endgültig gelöscht. ` +
            `Diese Aktion kann nicht rückgängig gemacht werden.`,
          confirmLabel: "Beides löschen",
        });
        if (both) {
          try {
            await api.delete(`/quotes/${q.id}?cascade=true`);
            toast.success(`Angebot ${q.quote_number} und Rechnung ${detail.invoice_number} gelöscht`);
            load();
          } catch (err2) {
            toast.error(formatApiError(err2));
          }
        }
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="crm-quotes">
      <div className="mb-5 md:mb-8 flex items-start justify-between gap-3 md:gap-4 flex-wrap">
        <div>
          <div className="hidden md:block text-xs font-medium text-[#6B7F3E] uppercase tracking-wider mb-2">CRM</div>
          <h1 className="hidden md:block font-display text-3xl font-semibold text-[#1A201A] tracking-tight">Angebote</h1>
          <p className="text-sm text-[#5C6B5D] mt-1">Erstellen, versenden und verfolgen Sie Angebote.</p>
        </div>
        <Button
          className="bg-[#6B7F3E] hover:bg-[#5A6B34] text-white"
          onClick={() => setCreating(true)}
          data-testid="quote-new-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Neues Angebot
        </Button>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
        {loading && <div className="p-8 text-sm text-[#5C6B5D]">Lädt…</div>}
        {!loading && items.length === 0 && (
          <div className="p-16 text-center text-sm text-[#5C6B5D]">
            Noch keine Angebote. Legen Sie das erste über „Neues Angebot" an.
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="crm-table w-full text-sm">
              <thead className="bg-[#F5F6F0] text-[11px] uppercase tracking-wider text-[#5C6B5D]">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Nr.</th>
                  <th className="text-left px-4 py-3 font-medium">Kunde</th>
                  <th className="text-right px-4 py-3 font-medium">Summe</th>
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                  <th className="text-right px-4 py-3 font-medium">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {items.map((q) => (
                  <tr key={q.id} data-testid={`quote-row-${q.id}`} className="border-t border-[#E5E7EB] hover:bg-[#FAFAF5]">
                    <td className="px-4 py-3 font-mono text-xs" data-label="Nr.">{q.quote_number}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{q.customer_name}</div>
                      <div className="text-xs text-[#5C6B5D]">{q.customer_email}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-medium" data-label="Summe">{fmt(q.total)}</td>
                    <td className="px-4 py-3" data-label="Status">
                      <Select value={q.status} onValueChange={(v) => changeStatus(q.id, v)}>
                        <SelectTrigger className="h-8 text-xs w-[150px]" data-testid={`quote-status-${q.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS_LABEL).map(([v, l]) => (
                            <SelectItem key={v} value={v}>{l}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 crm-td-actions">
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="outline" onClick={() => openPdf(q.id)} disabled={busyId === q.id} data-testid={`quote-pdf-${q.id}`}>
                          {busyId === q.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditing(q)}
                          disabled={busyId === q.id}
                          data-testid={`quote-edit-${q.id}`}
                          aria-label="Angebot bearbeiten"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          className="bg-[#6B7F3E] hover:bg-[#5A6B34] text-white"
                          onClick={() => sendQuote(q.id)}
                          disabled={busyId === q.id}
                          data-testid={`quote-send-${q.id}`}
                        >
                          {busyId === q.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1" /> Versenden</>}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => remove(q)}
                          disabled={busyId === q.id}
                          className="border-[#F5D9CC] text-[#B85C38] hover:bg-[#F5D9CC] hover:text-[#7A2E14]"
                          data-testid={`quote-delete-${q.id}`}
                          aria-label="Angebot löschen"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <QuoteCreateDialog open={creating} onOpenChange={setCreating} customers={customers} onCreated={load} />
      <QuoteCreateDialog
        open={!!editing}
        onOpenChange={(v) => { if (!v) setEditing(null); }}
        customers={customers}
        onCreated={load}
        quote={editing}
      />
      {confirmDialog}
    </div>
  );
}

const toFormItem = (it) => ({
  description: it.description || "",
  quantity: it.quantity ?? 1,
  unit: (!it.unit || it.unit === "Pauschal") ? defaultUnit(it.description) : it.unit,
  unit_price: it.unit_price ?? 0,
  custom: !LEISTUNG_OPTIONS.includes(it.description),
});

// quote gesetzt → Bearbeiten-Modus (Angebotsnummer bleibt unverändert)
function QuoteCreateDialog({ open, onOpenChange, customers, onCreated, quote = null }) {
  const [customerId, setCustomerId] = useState("");
  const [requestId, setRequestId] = useState(null);
  const [hintLeistungen, setHintLeistungen] = useState([]);
  const [items, setItems] = useState([{ description: "", quantity: 1, unit: "Pauschal", unit_price: 0, custom: false }]);
  const [taxRate, setTaxRate] = useState(19);
  const [validity, setValidity] = useState(30);
  const [intro, setIntro] = useState("");
  const [outro, setOutro] = useState("");
  const [winterdienstIncluded, setWinterdienstIncluded] = useState(false);
  const [kleinunternehmer, setKleinunternehmer] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (quote) {
      setCustomerId(quote.customer_id); setRequestId(quote.request_id || null); setHintLeistungen([]);
      setItems((quote.items || []).length ? quote.items.map(toFormItem) : [toFormItem({})]);
      // Bei § 19 UStG ist tax_rate 0 gespeichert – beim Abwählen wieder 19 % vorschlagen.
      setTaxRate(quote.kleinunternehmer ? 19 : quote.tax_rate);
      setValidity(quote.validity_days ?? 30);
      setIntro(quote.intro_text || ""); setOutro(quote.outro_text || "");
      setWinterdienstIncluded(!!quote.winterdienst_included);
      setKleinunternehmer(!!quote.kleinunternehmer);
      return;
    }
    // apply prefill if any
    const raw = localStorage.getItem("blessing_prefill_quote");
    if (raw) {
      try {
        const p = JSON.parse(raw);
        setCustomerId(p.customer_id);
        setRequestId(p.request_id);
        setHintLeistungen(p.leistungen || []);
      } catch { /* ignore */ }
      localStorage.removeItem("blessing_prefill_quote");
    } else {
      setCustomerId(""); setRequestId(null); setHintLeistungen([]);
    }
    // start with one blank line – NO auto-generated 0€ items
    setItems([{ description: "", quantity: 1, unit: "Pauschal", unit_price: 0, custom: false }]);
    setTaxRate(19); setValidity(30); setIntro(""); setOutro("");
    setWinterdienstIncluded(false);
    setKleinunternehmer(true);
  }, [open, quote]);

  const totals = useMemo(() => {
    const effectiveTax = kleinunternehmer ? 0 : (Number(taxRate) || 0);
    const sub = items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
    const tax = sub * effectiveTax / 100;
    return { sub, tax, total: sub + tax };
  }, [items, taxRate, kleinunternehmer]);

  const updateItem = (idx, patch) => {
    setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const addItem = () => setItems((a) => [...a, { description: "", quantity: 1, unit: "Pauschal", unit_price: 0, custom: false }]);
  const removeItem = (idx) => setItems((a) => a.filter((_, i) => i !== idx));

  const onLeistungChange = (idx, v) => {
    if (v === CUSTOM_LEISTUNG) {
      updateItem(idx, { description: "", custom: true });
    } else {
      // Einheit nur automatisch umstellen, solange sie nicht individuell geändert wurde.
      const { unit } = items[idx];
      const patch = { description: v, custom: false };
      if (!unit || unit === "Pauschal" || unit === "Monat") patch.unit = defaultUnit(v);
      updateItem(idx, patch);
    }
  };

  const addFromHint = (leistung) => {
    // Fill first empty item OR append a new one
    const emptyIdx = items.findIndex((it) => !it.description && !it.custom);
    const isKnown = LEISTUNG_OPTIONS.includes(leistung);
    const newItem = {
      description: isKnown ? leistung : leistung,
      quantity: 1,
      unit: defaultUnit(leistung),
      unit_price: 0,
      custom: !isKnown,
    };
    if (emptyIdx >= 0) {
      updateItem(emptyIdx, newItem);
    } else {
      setItems((a) => [...a, newItem]);
    }
  };

  async function save() {
    if (!customerId) { toast.error("Bitte Kunde wählen"); return; }
    const filled = items.filter((i) => i.description && i.description.trim().length > 0);
    if (filled.length === 0) {
      toast.error("Bitte mindestens eine Leistungsposition angeben"); return;
    }
    setSaving(true);
    try {
      const body = {
        customer_id: customerId,
        items: filled.map((i) => ({
          description: i.description.trim(),
          quantity: Number(i.quantity) || 1,
          unit: i.unit || defaultUnit(i.description),
          unit_price: Number(i.unit_price) || 0,
        })),
        tax_rate: Number(taxRate),
        validity_days: Number(validity),
        intro_text: intro || null,
        outro_text: outro || null,
        winterdienst_included: winterdienstIncluded,
        kleinunternehmer: kleinunternehmer,
      };
      if (quote) {
        await api.put(`/quotes/${quote.id}`, body);
        toast.success(`Angebot ${quote.quote_number} aktualisiert`);
      } else {
        await api.post("/quotes", { ...body, request_id: requestId || null });
        toast.success("Angebot erstellt");
      }
      onOpenChange(false);
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {quote ? `Angebot ${quote.quote_number} bearbeiten` : "Neues Angebot"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[65vh] overflow-y-auto p-1">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <Label>Kunde *</Label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger className="mt-1.5" data-testid="quote-customer-select">
                  <SelectValue placeholder="Kunde wählen" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name} — {c.address_city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Gültigkeit (Tage)</Label>
              <Input type="number" className="mt-1.5" value={validity} onChange={(e) => setValidity(e.target.value)} data-testid="quote-validity-input" />
            </div>
          </div>

          {hintLeistungen.length > 0 && (
            <div className="p-3 bg-[#F5F6F0] border border-[#E5E7EB] rounded-lg">
              <div className="text-[11px] font-medium uppercase tracking-wider text-[#6B7F3E] mb-1.5">
                Aus der Kundenanfrage
              </div>
              <div className="flex flex-wrap gap-1.5">
                {hintLeistungen.map((l) => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => addFromHint(l)}
                    className="text-xs bg-white border border-[#E5E7EB] hover:border-[#6B7F3E] hover:bg-[#E8ECE1] rounded-full px-2.5 py-1 text-[#2C3E2D] transition"
                    data-testid={`quote-hint-${l}`}
                  >
                    + {l}
                  </button>
                ))}
              </div>
              <div className="mt-2 text-[11px] text-[#5C6B5D]">
                Klicken Sie auf eine Leistung, um sie als Angebotsposition zu übernehmen. Nur ausgefüllte Positionen erscheinen auf dem PDF.
              </div>
            </div>
          )}

          <div className="border border-[#E5E7EB] rounded-lg">
            <div className="hidden md:grid grid-cols-12 gap-2 px-3 py-2 bg-[#F5F6F0] text-[11px] font-medium uppercase tracking-wider text-[#5C6B5D]">
              <div className="col-span-5">Leistung</div>
              <div className="col-span-2">Menge</div>
              <div className="col-span-2">Einheit</div>
              <div className="col-span-2 text-right">Einzelpreis</div>
              <div className="col-span-1" />
            </div>
            {items.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-[#E5E7EB] items-start">
                <div className="col-span-12 md:col-span-5 space-y-1.5">
                  <Select
                    value={it.custom ? CUSTOM_LEISTUNG : (LEISTUNG_OPTIONS.includes(it.description) ? it.description : "")}
                    onValueChange={(v) => onLeistungChange(i, v)}
                  >
                    <SelectTrigger className="h-8" data-testid={`quote-item-leistung-${i}`}>
                      <SelectValue placeholder="Leistung wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {LEISTUNG_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                      <SelectItem value={CUSTOM_LEISTUNG}>Individuelle Leistung…</SelectItem>
                    </SelectContent>
                  </Select>
                  {it.custom && (
                    <Input
                      className="h-8"
                      value={it.description}
                      onChange={(e) => updateItem(i, { description: e.target.value })}
                      placeholder="Eigene Bezeichnung eingeben"
                      data-testid={`quote-item-custom-${i}`}
                    />
                  )}
                </div>
                <Input
                  className="col-span-3 md:col-span-2 h-8" placeholder="Menge" type="number" step="0.5" data-testid={`quote-item-qty-${i}`}
                  value={it.quantity} onChange={(e) => updateItem(i, { quantity: e.target.value })}
                />
                <Input
                  className="col-span-4 md:col-span-2 h-8" placeholder="Einheit" data-testid={`quote-item-unit-${i}`}
                  value={it.unit} onChange={(e) => updateItem(i, { unit: e.target.value })}
                />
                <Input
                  className="col-span-3 md:col-span-2 h-8 text-right" placeholder="Preis" type="number" step="0.01" data-testid={`quote-item-price-${i}`}
                  value={it.unit_price} onChange={(e) => updateItem(i, { unit_price: e.target.value })}
                />
                <button
                  onClick={() => removeItem(i)}
                  className="col-span-2 md:col-span-1 flex justify-center pt-1 text-[#B85C38] hover:text-[#7A2E14]"
                  data-testid={`quote-item-remove-${i}`}
                  aria-label="Position entfernen"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <div className="px-3 py-2 border-t border-[#E5E7EB]">
              <Button variant="ghost" size="sm" onClick={addItem} data-testid="quote-item-add-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Position hinzufügen
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Anschreiben (optional)</Label>
              <Textarea rows={3} value={intro} onChange={(e) => setIntro(e.target.value)} placeholder="Persönlicher Text im Angebot…" data-testid="quote-intro-input" />
            </div>
            <div>
              <Label>Grußformel (optional)</Label>
              <Textarea rows={3} value={outro} onChange={(e) => setOutro(e.target.value)} placeholder="z.B. Auf gute Zusammenarbeit…" data-testid="quote-outro-input" />
            </div>
          </div>

          <label
            htmlFor="winterdienst-included"
            className="flex items-start gap-3 rounded-lg border border-[#E5E7EB] bg-[#F5F6F0] px-3 py-2.5 cursor-pointer hover:border-[#6B7F3E]/60 transition"
            data-testid="quote-winterdienst-row"
          >
            <Checkbox
              id="winterdienst-included"
              checked={winterdienstIncluded}
              onCheckedChange={(v) => setWinterdienstIncluded(v === true)}
              data-testid="quote-winterdienst-checkbox"
              className="mt-0.5"
            />
            <div className="text-sm text-[#2C3E2D] leading-snug">
              <div className="font-medium">Winterdienst inklusive</div>
              <div className="text-xs text-[#5C6B5D] mt-0.5">
                Nur relevant bei Rundum-Sorglos-Angeboten: aktiviert erscheint der
                Winterdienst in der Leistungsbeschreibung als enthalten, sonst
                unter „Nicht enthaltene Leistungen".
              </div>
            </div>
          </label>

          <label
            htmlFor="kleinunternehmer"
            className="flex items-start gap-3 rounded-lg border border-[#E5E7EB] bg-[#F5F6F0] px-3 py-2.5 cursor-pointer hover:border-[#6B7F3E]/60 transition"
            data-testid="quote-kleinunternehmer-row"
          >
            <Checkbox
              id="kleinunternehmer"
              checked={kleinunternehmer}
              onCheckedChange={(v) => setKleinunternehmer(v === true)}
              data-testid="quote-kleinunternehmer-checkbox"
              className="mt-0.5"
            />
            <div className="text-sm text-[#2C3E2D] leading-snug">
              <div className="font-medium">Keine Mehrwertsteuer (§ 19 UStG)</div>
              <div className="text-xs text-[#5C6B5D] mt-0.5">
                Aktiviert: Mehrwertsteuer wird auf 0 % gesetzt und die Klausel
                „Gemäß § 19 UStG wird keine Umsatzsteuer berechnet." erscheint auf dem PDF.
              </div>
            </div>
          </label>

          <div className="flex items-center justify-between border-t border-[#E5E7EB] pt-4">
            <div className="text-sm">
              MwSt.
              <Input
                type="number" className="inline-block w-20 h-8 ml-2 text-center"
                value={kleinunternehmer ? 0 : taxRate} onChange={(e) => setTaxRate(e.target.value)}
                disabled={kleinunternehmer}
                data-testid="quote-tax-input"
              /> %
              {kleinunternehmer && (
                <span className="ml-2 text-xs text-[#6B7F3E]">(§ 19 UStG)</span>
              )}
            </div>
            <div className="text-right text-sm space-y-0.5">
              <div>Zwischensumme: <span className="font-mono">{fmt(totals.sub)}</span></div>
              <div>MwSt: <span className="font-mono">{fmt(totals.tax)}</span></div>
              <div className="text-lg font-semibold text-[#2C3E2D]">Gesamt: <span className="font-mono">{fmt(totals.total)}</span></div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button
            className="bg-[#6B7F3E] hover:bg-[#5A6B34] text-white"
            onClick={save} disabled={saving}
            data-testid="quote-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileText className="w-4 h-4 mr-1" /> {quote ? "Änderungen speichern" : "Angebot speichern"}</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
